import { DETECTIVE_HIRING_TIME } from "@/constants/detective";
import { AgencyInfo } from "@/features/detective-agency";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import config, { SERVER_URL } from "@/config/config";
import { DetectiveHireStatus } from "@/constants/enum/enum";
import {
  claimCityProfit,
  finishHireDetective,
  getAgencyInfo,
  getCityDetectiveCost,
  getUserDetectiveHires,
  requestHireDetective,
  revealTarget,
  setCityDetectiveCost,
} from "@/helper/contractFunctions/MafiaDetectiveAgency";
import { approveGameCash } from "@/helper/contractFunctions/MafiaGameBank";
import { Detective } from "@/features/detective-agency";

export interface DetectiveState {
  isRequestingHire: boolean;
  isFinishingHire: boolean;
  isRevealingTarget: boolean;
  isApprovingGameCash: boolean;
  isLoadingCost: boolean;
  detectiveCost: number | null;
  isLoadingDetectives: boolean;
  detectiveListRequestId: string | null;
  detectives: Detective[];
  isLoadingOnChainDetectives: boolean;
  onChainDetectivesRequestId: string | null;
  onChainDetectives: Detective[];
  error: string | null;
  isLoadingAgencyInfo: boolean;
  agencyInfo: AgencyInfo | null;
  isClaimingProfit: boolean;
  isSettingCost: boolean;
}

const initialState: DetectiveState = {
  isRequestingHire: false,
  isFinishingHire: false,
  isRevealingTarget: false,
  isApprovingGameCash: false,
  isLoadingCost: false,
  detectiveCost: null,
  isLoadingDetectives: false,
  detectiveListRequestId: null,
  detectives: [],
  isLoadingOnChainDetectives: false,
  onChainDetectivesRequestId: null,
  onChainDetectives: [],
  error: null,
  isLoadingAgencyInfo: false,
  agencyInfo: null,
  isClaimingProfit: false,
  isSettingCost: false,
};

export const requestHireDetectiveAction = createAsyncThunk(
  "detective/requestHireDetectiveAction",
  async ({
    account,
    target,
    detectivesCount,
    message,
    signature,
  }: {
    account: string;
    target: string;
    detectivesCount: number;
    message: string;
    signature: string;
  }) => {
    const result = await requestHireDetective(
      account,
      target,
      detectivesCount,
      message,
      signature
    );
    return {
      transactionHash: result.transactionHash || "",
      hireId: result.hireId,
      confirmedBlockNumber: result.blockNumber,
    };
  }
);

export const finishHireDetectiveAction = createAsyncThunk(
  "detective/finishHireDetectiveAction",
  async ({
    account,
    hireId,
  }: {
    account: string;
    hireId: number;
  }) => {
    const result = await finishHireDetective(account, hireId);
    return result;
  }
);

export const revealTargetAction = createAsyncThunk(
  "detective/revealTargetAction",
  async ({
    account,
    hireId,
  }: {
    account: string;
    hireId: number;
  }) => {
    const result = await revealTarget(account, hireId);
    return result;
  }
);

export const approveGameCashAction = createAsyncThunk(
  "detective/approveGameCashAction",
  async ({ cashAmount, account }: { cashAmount: number; account: string }) => {
    const data = await approveGameCash(
      cashAmount,
      config.detectiveAgency,
      account
    );
    return data.transactionHash || "";
  }
);

export const getDetectiveCostAction = createAsyncThunk(
  "detective/getDetectiveCostAction",
  async ({ cityId }: { cityId: number }) => {
    const cost = await getCityDetectiveCost(cityId);
    return cost;
  }
);

export const getAgencyInfoAction = createAsyncThunk(
  "detective/getAgencyInfoAction",
  async ({ cityId }: { cityId: number }) => {
    const info = await getAgencyInfo(cityId);
    return info;
  }
);

export const claimCityProfitAction = createAsyncThunk(
  "detective/claimCityProfitAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    const result = await claimCityProfit(cityId, account);
    return result.transactionHash || "";
  }
);

export const setCityDetectiveCostAction = createAsyncThunk(
  "detective/setCityDetectiveCostAction",
  async ({ account, cityId, cost }: { account: string; cityId: number; cost: number }) => {
    const result = await setCityDetectiveCost(cityId, cost, account);
    return result.transactionHash || "";
  }
);

export const getUserDetectiveHiresAction = createAsyncThunk(
  "detective/getUserDetectiveHiresAction",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const hires = await getUserDetectiveHires(account, signMsg, signature);
    return hires;
  }
);

export const mapDetectiveListItem = (item: any, currentTime: number): Detective => {
  const rawStatus = Number(item.status ?? DetectiveHireStatus.Pending);
  const requestedTimestamp = Number(item.requestedTimestamp || 0); // startedAt
  const finishedTimestamp = item.finishedTimestamp ? Number(item.finishedTimestamp) : null;
  const targetNumber = item.targetNumber ? Number(item.targetNumber) : null;
  const isTargetRevealed = item.targetCityId != null;
  const targetCityId = isTargetRevealed ? Number(item.targetCityId) : null;

  const canKillUntilTime = item.canKillUntilTime || null;

  let canReveal = false;
  let displayStatus: "searching" | "found" | "failed" | "revealed" = "searching";

  if (rawStatus === DetectiveHireStatus.Pending) {
    // Pending: show as "searching"
    displayStatus = "searching";
    canReveal = false;
  } else if (rawStatus === DetectiveHireStatus.Failed) {
    // Failed: wait DETECTIVE_HIRING_TIME from finishedTimestamp, then show "failed"
    if (finishedTimestamp) {
      const revealTime = finishedTimestamp + DETECTIVE_HIRING_TIME;
      if (currentTime >= revealTime) {
        displayStatus = "failed";
      } else {
        displayStatus = "searching";
      }
    } else {
      // If finishedTimestamp is not available yet, keep as searching
      displayStatus = "searching";
    }
    canReveal = false;
  } else if (rawStatus === DetectiveHireStatus.Success) {
    // Success: check if we can reveal
    if (finishedTimestamp) {
      // Check if target is revealed
      if (isTargetRevealed) {
        displayStatus = "revealed";
        canReveal = false;
      } else {
        // Check if we can reveal (wait targetNumber * 60 seconds from finishedTimestamp)
        if (targetNumber !== null && targetNumber > 0) {
          const foundTime = finishedTimestamp + (targetNumber * 60);
          if (currentTime >= foundTime) {
            displayStatus = "found";
            canReveal = true;
          } else {
            displayStatus = "searching";
            canReveal = false;
          }
        } else {
          displayStatus = "searching";
          canReveal = false;
        }
      }
    } else {
      // If finishedTimestamp is not available yet, keep as searching
      displayStatus = "searching";
      canReveal = false;
    }
  }

  // Create detective name from count
  const detectiveName = item.detectivesCount > 1
    ? `${item.detectivesCount} Detectives`
    : `1 Detective`;

  const targetAddress = item.target || "";
  const targetName = item.targetName || "";
  const targetDisplay = targetName || targetAddress;

  return {
    id: Number(item.id) || 0,
    name: detectiveName,
    target: targetDisplay,
    targetAddress,
    targetName,
    status: displayStatus,
    rawStatus,
    requestedTimestamp,
    finishedTimestamp,
    targetNumber,
    isTargetRevealed,
    canReveal,
    canKillUntilTime,
    targetCityId,
  };
};

export const getDetectiveListAction = createAsyncThunk(
  "detective/getDetectiveListAction",
  async () => {
    const response = await axios.get(`${SERVER_URL}/business/detective`);
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds

    return (response.data || []).map((item: any) => mapDetectiveListItem(item, currentTime));
  }
);

export const detectiveSlice = createSlice({
  name: "detective",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDetectives: (state) => {
      state.detectives = [];
      state.isLoadingDetectives = false;
      state.detectiveListRequestId = null;
      state.onChainDetectives = [];
      state.isLoadingOnChainDetectives = false;
      state.onChainDetectivesRequestId = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Request Hire Detective
      .addCase(requestHireDetectiveAction.pending, (state) => {
        state.isRequestingHire = true;
        state.error = null;
      })
      .addCase(requestHireDetectiveAction.fulfilled, (state) => {
        state.isRequestingHire = false;
      })
      .addCase(requestHireDetectiveAction.rejected, (state, { error }) => {
        state.isRequestingHire = false;
        state.error = error.message || "Failed to request hire detective";
      })
      // Finish Hire Detective
      .addCase(finishHireDetectiveAction.pending, (state) => {
        state.isFinishingHire = true;
        state.error = null;
      })
      .addCase(finishHireDetectiveAction.fulfilled, (state, { payload }) => {
        // Update the detective with the result from finishHireDetective
        const currentTime = Math.floor(Date.now() / 1000);
        const finishedTimestamp = payload.timestamp || currentTime;

        state.detectives = state.detectives.map((detective) => {
          if (detective.id === payload.hireId) {
            // Calculate status based on result and time
            let displayStatus: "searching" | "found" | "failed" | "revealed" = "searching";
            let canReveal = false;

            if (payload.isTargetFound) {
              // Success case
              // Check if we can reveal (wait targetNumber * 60 seconds from finishedTimestamp)
              if (payload.targetNumber !== null && payload.targetNumber > 0) {
                const foundTime = finishedTimestamp + (payload.targetNumber * 60);
                if (currentTime >= foundTime) {
                  displayStatus = "found";
                  canReveal = true;
                } else {
                  displayStatus = "searching";
                  canReveal = false;
                }
              } else {
                displayStatus = "searching";
                canReveal = false;
              }
            } else {
              // Failed case
              const revealTime = finishedTimestamp + DETECTIVE_HIRING_TIME;
              if (currentTime >= revealTime) {
                displayStatus = "failed";
              } else {
                displayStatus = "searching";
              }
              canReveal = false;
            }

            return {
              ...detective,
              status: displayStatus,
              rawStatus: payload.isTargetFound ? DetectiveHireStatus.Success : DetectiveHireStatus.Failed,
              finishedTimestamp,
              targetNumber: payload.targetNumber,
              canReveal,
            };
          }
          return detective;
        });
        state.isFinishingHire = false;
      })
      .addCase(finishHireDetectiveAction.rejected, (state, { error }) => {
        state.isFinishingHire = false;
        state.error = error.message || "Failed to finish hire detective";
      })
      // Reveal Target
      .addCase(revealTargetAction.pending, (state) => {
        state.isRevealingTarget = true;
        state.error = null;
      })
      .addCase(revealTargetAction.fulfilled, (state, { payload }) => {
        state.isRevealingTarget = false;
        state.detectives = state.detectives.map((detective) => {
          if (detective.id === payload.hireId) {
            return {
              ...detective,
              status: "revealed",
              isTargetRevealed: true,
              targetCityId: payload.targetCityId,
              canKillUntilTime: payload.canKillUntilTime,
            };
          }
          return detective;
        });
      })
      .addCase(revealTargetAction.rejected, (state, { error }) => {
        state.isRevealingTarget = false;
        state.error = error.message || "Failed to reveal target";
      })
      // Approve Game Cash
      .addCase(approveGameCashAction.pending, (state) => {
        state.isApprovingGameCash = true;
        state.error = null;
      })
      .addCase(approveGameCashAction.fulfilled, (state) => {
        state.isApprovingGameCash = false;
      })
      .addCase(approveGameCashAction.rejected, (state, { error }) => {
        state.isApprovingGameCash = false;
        state.error = error.message || "Failed to approve game cash";
      })
      // Get Detective Cost
      .addCase(getDetectiveCostAction.pending, (state) => {
        state.isLoadingCost = true;
        state.error = null;
      })
      .addCase(getDetectiveCostAction.fulfilled, (state, { payload }) => {
        state.isLoadingCost = false;
        state.detectiveCost = payload;
      })
      .addCase(getDetectiveCostAction.rejected, (state, { error }) => {
        state.isLoadingCost = false;
        state.error = error.message || "Failed to get detective cost";
      })
      // Get Detective List
      .addCase(getDetectiveListAction.pending, (state, { meta }) => {
        state.isLoadingDetectives = true;
        state.detectiveListRequestId = meta.requestId;
        state.error = null;
      })
      .addCase(getDetectiveListAction.fulfilled, (state, { meta, payload }) => {
        if (state.detectiveListRequestId !== meta.requestId) return;
        state.isLoadingDetectives = false;
        state.detectiveListRequestId = null;
        state.detectives = payload;
      })
      .addCase(getDetectiveListAction.rejected, (state, { error, meta }) => {
        if (state.detectiveListRequestId !== meta.requestId) return;
        state.isLoadingDetectives = false;
        state.detectiveListRequestId = null;
        state.error = error.message || "Failed to get detective list";
      })
      // Get User Detective Hires (on-chain)
      .addCase(getUserDetectiveHiresAction.pending, (state, { meta }) => {
        state.isLoadingOnChainDetectives = true;
        state.onChainDetectivesRequestId = meta.requestId;
        state.error = null;
      })
      .addCase(getUserDetectiveHiresAction.fulfilled, (state, { meta, payload }) => {
        if (state.onChainDetectivesRequestId !== meta.requestId) return;
        state.isLoadingOnChainDetectives = false;
        state.onChainDetectivesRequestId = null;
        state.onChainDetectives = payload;
      })
      .addCase(getUserDetectiveHiresAction.rejected, (state, { error, meta }) => {
        if (state.onChainDetectivesRequestId !== meta.requestId) return;
        state.isLoadingOnChainDetectives = false;
        state.onChainDetectivesRequestId = null;
        state.error = error.message || "Failed to get detective hires";
      })
      // Get Agency Info
      .addCase(getAgencyInfoAction.pending, (state) => {
        state.isLoadingAgencyInfo = true;
        state.error = null;
      })
      .addCase(getAgencyInfoAction.fulfilled, (state, { payload }) => {
        state.isLoadingAgencyInfo = false;
        state.agencyInfo = payload;
      })
      .addCase(getAgencyInfoAction.rejected, (state, { error }) => {
        state.isLoadingAgencyInfo = false;
        state.error = error.message || "Failed to get agency info";
      })
      // Claim City Profit
      .addCase(claimCityProfitAction.pending, (state) => {
        state.isClaimingProfit = true;
        state.error = null;
      })
      .addCase(claimCityProfitAction.fulfilled, (state) => {
        state.isClaimingProfit = false;
      })
      .addCase(claimCityProfitAction.rejected, (state, { error }) => {
        state.isClaimingProfit = false;
        state.error = error.message || "Failed to claim profit";
      })
      // Set City Detective Cost
      .addCase(setCityDetectiveCostAction.pending, (state) => {
        state.isSettingCost = true;
        state.error = null;
      })
      .addCase(setCityDetectiveCostAction.fulfilled, (state) => {
        state.isSettingCost = false;
      })
      .addCase(setCityDetectiveCostAction.rejected, (state, { error }) => {
        state.isSettingCost = false;
        state.error = error.message || "Failed to set detective cost";
      })
  },
});

export const { clearError, clearDetectives } = detectiveSlice.actions;
export default detectiveSlice.reducer;
